/* eslint-disable no-nested-ternary */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHref } from 'react-router-dom';
import { PsVersion } from '../../../../../api/model';
import { useGetPsVersionList } from '../../../../../api/pss/pss';
import { useDefaultOptions } from '../../../../../hooks/api';
import { toString } from '../../../../../utils/string';
import ErrorComponent from '../../../../atoms/ErrorComponent';
import WaitComponent from '../../../../atoms/WaitComponent';
import Modal from '../../../../modules/Modal';

interface Props {
  /** モーダル表示・非表示状態。 */
  isOpen: boolean;

  /** モーダル表示・非表示状態設定関数。 */
  setIsOpen: (isOpen: boolean) => void;

  /** PS ツリーで右クリックで選択された PS のバージョン ID。 */
  selectedPsVersionId: number;

  /** PS ツリーで右クリックで選択されたPS種別 */
  selectedPsType: number;
}

// PS 閲覧画面で使用。
/** PS 差分指定ダイアログ (PS 比較画面へ遷移する用)。 */
function PsDiffDialog(
  {
    isOpen,
    setIsOpen,
    selectedPsVersionId,
    selectedPsType,
  }: Props
) {

  // i18n 用。
  const {
    t,
    i18n,
  } = useTranslation();

  const defaultOptions = useDefaultOptions(i18n.language);

  /** 旧バージョン ドロップダウンの値の state。 */
  const [olderPsVersionId, setOlderPsVersionId] = useState<number>(-1);
  /** 新バージョン ドロップダウンの値の state。 */
  const [newerPsVersionId, setNewerPsVersionId] = useState<number>(-1);

  // console.log('olderPsVersionId:', olderPsVersionId);
  // console.log('newerPsVersionId:', newerPsVersionId);

  const diffType = selectedPsType > 1 ? "diff" : "difftree";
  const href = useHref(`/${i18n.language}/ps/${diffType}/${olderPsVersionId}/${newerPsVersionId}`);

  // ドロップダウンのために。
  /** PS バージョン一覧取得 API 呼び出し。 */
  const getPsVersionListResponse = useGetPsVersionList(selectedPsVersionId, defaultOptions);

  useEffect(
    () => {
      if (
        (getPsVersionListResponse.isSuccess)
        &&
        (Array.isArray(getPsVersionListResponse.data?.data))
        &&
        (getPsVersionListResponse.data.data.length > 0)
      ) {
        // リストの最初の値にセットする。
        setOlderPsVersionId(getPsVersionListResponse.data.data[0].ps_version_id as number);
        setNewerPsVersionId(getPsVersionListResponse.data.data[0].ps_version_id as number);
      }
    },
    [getPsVersionListResponse.data?.data, getPsVersionListResponse.isSuccess]
  );

  /** バージョン選択ドロップダウンの options。 */
  const psVersionOptions = (
    (
      (getPsVersionListResponse.data?.data)
      &&
      Array.isArray(getPsVersionListResponse.data.data)
    ) ?
      getPsVersionListResponse.data.data
      :
      []
  ).map(
    (psVersion: PsVersion) =>
    (
      <option
        key={psVersion.ps_version_id}
        value={psVersion.ps_version_id}
      >
        {`${Number(psVersion.ps_version_no)} (${toString(psVersion.release_at)})`}
      </option>
    )
  );

  return (
    <Modal
      active={isOpen}
      additionalClassName='popupfr644 pt30'
      displayCloseButton
      onClose={() => {
        setIsOpen(false);
      }}
    >
      {
        // モーダル内部にメッセージを表示。
        (getPsVersionListResponse.isLoading) ?
          (
            <WaitComponent />
          )
          :
          (
            getPsVersionListResponse.isError
            ||
            !(getPsVersionListResponse.data?.data)
          ) ?
            (
              <ErrorComponent error={getPsVersionListResponse.error} />
            )
            :
            (
              <>
                <div className='windowsep'>
                  <div>
                    <label>
                      {t('旧バージョン')}
                    </label>
                    <span className='dropdown'>
                      <select
                        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                          setOlderPsVersionId(parseInt(event.target.value, 10));
                        }}
                        value={olderPsVersionId}
                      >
                        {psVersionOptions}
                      </select>
                    </span>
                  </div>
                  <div>
                    <label>
                      {t('新バージョン')}
                    </label>
                    <span className='dropdown'>
                      <select
                        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                          setNewerPsVersionId(parseInt(event.target.value, 10));
                        }}
                        value={newerPsVersionId}
                      >
                        {psVersionOptions}
                      </select>
                    </span>
                  </div>
                </div>

                <div className='btnfr btnfr2 mb0 mt0'>
                  <div>
                    <span
                      className='btn2 btnbg-g mr10'
                      onClick={
                        () => {
                          setIsOpen(false);
                        }
                      }
                    >
                      <span className='modal-close2'>
                        {t('キャンセル')}
                      </span>
                    </span>
                    <span>
                      <input
                        type='button'
                        value={toString(t('表示'))}
                        className='btn1 lsp1'
                        disabled={
                          (olderPsVersionId < 0)
                          ||
                          (newerPsVersionId < 0)
                          ||
                          // 旧バージョンが新バージョンと同じか大きい場合 disable。
                          (olderPsVersionId >= newerPsVersionId)
                        }
                        onClick={
                          () => {
                            // 選択した旧バージョン、新バージョンの PS バージョン ID をパラメータに設定し、PS 比較画面を新しいタブで開く。
                            window.open(href, '_blank');
                          }
                        }
                      />
                    </span>
                  </div>
                </div>
              </>
            )
      }
    </Modal>
  );
}

export default PsDiffDialog;